import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon, Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import api from 'api/client';

const accent = '#f97316';
const cardSx = {
  border: '1px solid',
  borderColor: alpha(accent, 0.35),
  borderRadius: 3,
  boxShadow: 'none'
};
const buttonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: accent,
  border: `1px solid ${accent}`,
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#fb923c' }
};

const defaults = {
  enabled: false,
  min_severity: 'high',
  dedup_window_days: 180,
  max_cases_per_scan: 25,
  auto_assign_to_owner: true,
  reopen_resolved: false,
  include_duplicate_findings: false,
  notify_owner: true,
  due_date_from_sla: true,
  include_tools: [],
  exclude_tools: [],
  scan_types: [],
  title_prefix: '[Auto]',
  label: 'auto-remediation'
};

const severityOptions = ['critical', 'high', 'medium', 'low', 'info'];

function parseError(error, fallback) {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  if (payload.detail) return payload.detail;
  const key = Object.keys(payload)[0];
  if (!key) return fallback;
  const value = payload[key];
  if (Array.isArray(value)) return value[0];
  if (typeof value === 'string') return value;
  return fallback;
}

function parseCsv(raw) {
  return String(raw || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

const AdminCaseAutomationPage = () => {
  const [policy, setPolicy] = useState(defaults);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [includeToolsRaw, setIncludeToolsRaw] = useState('');
  const [excludeToolsRaw, setExcludeToolsRaw] = useState('');
  const [scanTypesRaw, setScanTypesRaw] = useState('');

  const loadPolicy = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await api.get('/api/admin/settings/case-automation/');
      const next = { ...defaults, ...(data || {}) };
      setPolicy(next);
      setIncludeToolsRaw((next.include_tools || []).join(', '));
      setExcludeToolsRaw((next.exclude_tools || []).join(', '));
      setScanTypesRaw((next.scan_types || []).join(', '));
    } catch (err) {
      setError(parseError(err, 'Failed to load case automation policy.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const savePolicy = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...policy,
        dedup_window_days: Number(policy.dedup_window_days || defaults.dedup_window_days),
        max_cases_per_scan: Number(policy.max_cases_per_scan || defaults.max_cases_per_scan),
        include_tools: parseCsv(includeToolsRaw),
        exclude_tools: parseCsv(excludeToolsRaw),
        scan_types: parseCsv(scanTypesRaw)
      };
      const { data } = await api.put('/api/admin/settings/case-automation/', payload);
      const next = { ...defaults, ...(data || {}) };
      setPolicy(next);
      setIncludeToolsRaw((next.include_tools || []).join(', '));
      setExcludeToolsRaw((next.exclude_tools || []).join(', '));
      setScanTypesRaw((next.scan_types || []).join(', '));
      setSuccess('Case automation policy updated.');
    } catch (err) {
      setError(parseError(err, 'Failed to update case automation policy.'));
    } finally {
      setSaving(false);
    }
  };

  const policySummary = useMemo(() => {
    if (!policy.enabled) return 'Automation disabled';
    return `Create up to ${policy.max_cases_per_scan} case(s) per scan from ${policy.min_severity}+ findings.`;
  }, [policy]);

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2.2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: accent,
                    bgcolor: alpha(accent, 0.12)
                  }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Case Automation
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Configure automatic remediation case creation from scan findings.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadPolicy} disabled={loading || saving} sx={buttonSx}>
                  Reload
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={savePolicy} disabled={loading || saving} sx={buttonSx}>
                  Save
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
        {(loading || saving) && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: accent } }} />}
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={cardSx}>
            <CardHeader title="Policy" titleTypographyProps={{ variant: 'h6', fontWeight: 700 }} />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={<Switch checked={Boolean(policy.enabled)} onChange={(event) => setPolicy((prev) => ({ ...prev, enabled: event.target.checked }))} />}
                    label="Enable case automation"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Minimum severity"
                    value={policy.min_severity}
                    onChange={(event) => setPolicy((prev) => ({ ...prev, min_severity: event.target.value }))}
                  >
                    {severityOptions.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Dedup window (days)"
                    value={policy.dedup_window_days}
                    onChange={(event) => setPolicy((prev) => ({ ...prev, dedup_window_days: event.target.value }))}
                    inputProps={{ min: 1, max: 3650 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max new cases per scan"
                    value={policy.max_cases_per_scan}
                    onChange={(event) => setPolicy((prev) => ({ ...prev, max_cases_per_scan: event.target.value }))}
                    inputProps={{ min: 1, max: 500 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title prefix"
                    value={policy.title_prefix || ''}
                    onChange={(event) => setPolicy((prev) => ({ ...prev, title_prefix: event.target.value }))}
                    helperText="Prepended to auto-created case titles."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label"
                    value={policy.label || ''}
                    onChange={(event) => setPolicy((prev) => ({ ...prev, label: event.target.value }))}
                    helperText="Applied to auto-created cases."
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Include tools (comma separated)"
                    value={includeToolsRaw}
                    onChange={(event) => setIncludeToolsRaw(event.target.value)}
                    helperText="Leave empty to allow all tools."
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Exclude tools (comma separated)"
                    value={excludeToolsRaw}
                    onChange={(event) => setExcludeToolsRaw(event.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Allowed scan types (comma separated)"
                    value={scanTypesRaw}
                    onChange={(event) => setScanTypesRaw(event.target.value)}
                    helperText="Leave empty to allow all scan types."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(policy.auto_assign_to_owner)}
                        onChange={(event) => setPolicy((prev) => ({ ...prev, auto_assign_to_owner: event.target.checked }))}
                      />
                    }
                    label="Assign case to owner"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch checked={Boolean(policy.reopen_resolved)} onChange={(event) => setPolicy((prev) => ({ ...prev, reopen_resolved: event.target.checked }))} />
                    }
                    label="Reopen resolved cases"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(policy.include_duplicate_findings)}
                        onChange={(event) => setPolicy((prev) => ({ ...prev, include_duplicate_findings: event.target.checked }))}
                      />
                    }
                    label="Include duplicate findings"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={<Switch checked={Boolean(policy.notify_owner)} onChange={(event) => setPolicy((prev) => ({ ...prev, notify_owner: event.target.checked }))} />}
                    label="Send owner notifications"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch checked={Boolean(policy.due_date_from_sla)} onChange={(event) => setPolicy((prev) => ({ ...prev, due_date_from_sla: event.target.checked }))} />
                    }
                    label="Set due date from SLA policy"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={cardSx}>
            <CardHeader title="Summary" titleTypographyProps={{ variant: 'h6', fontWeight: 700 }} />
            <Divider />
            <CardContent>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {policySummary}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Include tools: {(policy.include_tools || []).join(', ') || 'All'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Exclude tools: {(policy.exclude_tools || []).join(', ') || 'None'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Scan types: {(policy.scan_types || []).join(', ') || 'All'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AdminCaseAutomationPage;
